import type { SettingsTranslations } from "../../types/settings";

export const settings: SettingsTranslations = {
  title: "User Settings",
  security: "Security",
  organizationTitle: "Organisation settings",
  organizationSubtitle: "Manage your organisation's details.",
  userManagementSubtitle: "Invite users and manage their roles.",
  usage: {
    title: "Usage",
    subtitle: "Storage used across your buildings and projects.",
    used: "used",
    of: "of",
    near: "Storage nearly full",
    full: "Storage full",
    unlimited: "Unlimited",
    byProject: "Storage by project",
    noProjects: "No visible projects.",
    shared: "Files shared between projects count in each project; the building counts them once.",
    loading: "Loading storage usage…",
    loadError: "Could not load storage usage. Please refresh to try again.",
    retry: "Try again",
    empty: "No accessible buildings in this organisation.",
  },
  general: "General",
  languageEnglish: "English",
  languageEstonian: "Eesti",
  organization: {
    details: "Organisation details",
    logo: "Organisation logo",
    logoDescription: "Shown in the organisation switcher and on the invitation emails you send.",
    logoEmpty: "Upload a logo for your organisation.",
    uploadLogo: "Upload logo",
    replaceLogo: "Replace logo",
    removeLogo: "Remove logo",
    logoUploaded: "Organisation logo updated",
    logoRemoved: "Organisation logo removed",
    logoTooLarge: "Image must be 2 MB or smaller",
    logoInvalidType: "Only PNG, JPEG, or WebP images are allowed",
    logoUploadFailed: "Could not upload the organisation logo. Please try again.",
  },
  personal: {
    title: "Personal Information",
    description: "Update your personal details and account settings.",
    basicInfo: "Basic Information",
    contactInfo: "Contact Information",
    professionalInfo: "Professional Information",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    phonePlaceholder: "+1 234 567 8900",
    jobTitle: "Job Title",
    jobTitlePlaceholder: "e.g., Senior Engineer",
    company: "Company",
    companyPlaceholder: "e.g., Acme Corp",
    profileUpdated: "Profile updated",
    profileUpdatedDesc: "Your profile has been updated successfully.",
    updateFailed: "Update failed",
    updateFailedDesc: "Could not update your profile. Please try again.",
    notAuthenticated: "User not authenticated",
    profileNotLoaded: "Profile not loaded. Please reload the page.",
    namesRequired: "First name and last name are required",
    profilePicture: "Profile picture",
    editProfilePicture: "Upload",
    removeProfilePicture: "Remove photo",
    profilePictureUploaded: "Profile picture updated",
    profilePictureRemoved: "Profile picture removed",
    profilePictureTooLarge: "Image must be 2 MB or smaller",
    profilePictureInvalidType: "Only PNG, JPEG, or WebP images are allowed",
    profilePictureUploadFailed: "Could not upload profile picture. Please try again.",
    cropTitle: "Adjust photo",
    cropDescription: "Drag to reposition and use the slider to zoom.",
    cropZoom: "Zoom",
    cropSave: "Save",
    cropCancel: "Cancel",
    avatarColor: "Avatar colour",
    avatarColorDescription:
      "Pick the colour for your initials avatar when you have no profile picture.",
    customColor: "Custom colour",
    customColorPlaceholder: "#ef4129",
    pickCustomColor: "Pick a custom colour",
    colorPickerHexLabel: "Hex colour",
    invalidColor: "Enter a valid hex colour, e.g. #ef4129.",
    notifications: "Email notifications",
    pmDigestLabel: "Send me PM activity digest emails",
    pmDigestDescription:
      "Every 15 minutes we summarise new assignments, @-mentions, and comments on your issues. Untick to stop receiving these.",
    messageBoardLabel: "Send me message board notifications",
    messageBoardDescription:
      "Get an email when someone posts on a project message board you have access to. Untick to stop receiving these.",
  },
  securitySection: {
    changePassword: "Change Password",
    changePasswordDesc: "Update your account password to keep your account secure.",
    newPassword: "New Password",
    newPasswordPlaceholder: "Enter new password",
    confirmPassword: "Confirm Password",
    confirmPasswordPlaceholder: "Confirm new password",
    updatePassword: "Update Password",
    passwordRequired: "Password is required",
    passwordMinLength: "Password must be at least 6 characters",
    passwordMismatch: "Passwords do not match",
    passwordUpdated: "Password updated",
    passwordUpdatedDesc: "Your password has been changed successfully.",
    updateFailed: "Update failed",
    updateFailedDesc: "Could not update your password. Please try again.",
    twoFA: "Two-Factor Authentication",
    twoFADesc: "Add an extra layer of security to your account with two-factor authentication.",
    twoFAInfo:
      "Two-factor authentication (2FA) adds an additional layer of security to your account by requiring a verification code from your mobile device when you sign in.",
    twoFAStatus: "2FA Status",
    twoFANotConfigured: "Not configured",
    enable2FA: "Enable 2FA",
    comingSoon: "Coming Soon",
    twoFAInDevelopment: "Two-factor authentication setup is currently in development.",
  },
  invitations: {
    title: "Organisation Invitations",
    description: "Manage pending and sent invitations to your organisation",
    pageSubtitle: "Pending invitations waiting for your response.",
    noPendingDesc: "When someone invites you to join an organisation, it'll show up here.",
    inviteUser: "Invite User",
    linkCopied: "Invite link copied to clipboard",
    copyLink: "Copy Link",
    resend: "Resend",
    accepted: "Accepted",
    expired: "Expired",
    pending: "Pending",
    empty: "No invitations sent yet",
    emptyDesc: "Start by inviting users to join your organisation",
    failedLoadInvites: "Failed to load invites",
    expiresIn: "Expires {time}",
    expiredAgo: "Expired {time}",
    deleteInviteTitle: "Delete invitation?",
    deleteInviteDescription:
      "This removes the invitation for {email}. The invite link will stop working.",
    deleteInviteSuccess: "Invitation deleted",
    deleteInviteFailed: "Failed to delete invitation",
    acceptedSuccess: "You've joined the organisation successfully.",
    acceptFailed: "Failed to accept invitation",
    accept: "Accept",
    decline: "Decline",
    declineInviteTitle: "Decline invitation?",
    declineInviteDescription:
      "This declines the invitation to join {org}. An administrator can invite you again later.",
    declinedSuccess: "Invitation declined",
    declineFailed: "Failed to decline invitation",
    invited: "Invited",
    invitedBy: "By:",
    invitedRelativeBy: "Invited {time} by {name}",
    invitedByName: "Invited by {name}",
    moreActions: "More actions",
    revoke: "Revoke",
    revokeInviteTitle: "Revoke invitation?",
    revokeInviteDescription:
      "This revokes the invitation for {email}. The invite link will stop working.",
    revokeInviteSuccess: "Invitation revoked",
    revokeInviteFailed: "Failed to revoke invitation",
    clearAllExpired: "Clear all expired",
    clearExpiredTitle: "Clear expired invitations?",
    clearExpiredDescription:
      "This removes all {count} expired invitations. Their invite links already stopped working.",
    clearExpiredSuccess: "Expired invitations cleared",
    clearExpiredPartial: "Some expired invitations could not be cleared",
    clearExpiredFailed: "Failed to clear expired invitations",
    inviteDescription: "Send an invitation to join {org}.",
    inviteDescriptionNoOrganisation: "Send an invitation to join your organisation.",
    emailLabel: "Email address",
    emailPlaceholder: "user@example.com",
    rolePlaceholder: "Select a role",
    cancel: "Cancel",
    send: "Send invitation",
    sending: "Sending…",
    sent: "Invitation sent",
    sendFailed: "Failed to create invitation",
    noOrganisation: "No organisation selected",
    invalidEmail: "Please enter a valid email address",
    chooseRole: "Please choose a role",
  },
  roles: {
    userUpdated: "User updated successfully",
    updateError: "Error",
    updateFailed: "Failed to update user",
    mustAssignOwner: "Assign another org owner before downgrading yourself",
    errorLoading: "Error loading users",
    role: "Role",
    roleLabels: {
      org_owner: "Owner",
      org_admin: "Admin",
      subcontractor: "Member",
    },
    roleUpdated: "Role updated to {role}",
    roleUpdateFailed: "Failed to update role",
    memberRemoved: "Member removed",
    memberRemoveFailed: "Failed to remove member",
    removeMemberTitle: "Remove member",
    removeMemberDescription:
      "Remove {email} from this organisation? They will lose all access to it. This does not delete their account.",
    buildingAssignments: "Building Assignments",
    status: "Status",
    selectRole: "Select role",
    roleDescriptions: {
      org_admin: "Full access to every project and to organisation settings",
      subcontractor: "Access only to the projects they are given",
    },
    selectBuildings: "Select buildings (optional)",
    allBuildings: "All buildings",
    selectStatus: "Select status",
    active: "Active",
    inactive: "Inactive",
    save: "Save",
    refresh: "Refresh",
    manageProjectsTitle: "Manage project access",
    manageProjectsDescription: "Choose which projects {name} can access.",
    projectsLabel: "Projects this member can access",
    loadingProjects: "Loading projects…",
    projectsLoadFailed: "Could not load projects.",
    projectsRefreshFailed:
      "Could not refresh the project list. It may be out of date, so retry before sending.",
    projectsNotLoaded: "The project list is still loading. Wait for it, then send the invitation.",
    projectNoLongerAvailable:
      "A project you selected is no longer available. Check your selection and send again.",
    projectsNoLongerAvailable:
      "{count} projects you selected are no longer available. Check your selection and send again.",
    projectsNoLongerOfferable:
      "Nothing was saved. No longer available: {projects}. Those ticks have been cleared — your other changes are still here, so save again to apply them.",
    projectsChangedElsewhere:
      "Nothing was saved. Someone else changed this member's access while this was open, so your changes were made against an out-of-date list. It has been reloaded — please make them again.",
    projectUnavailable: "archived or hidden",
    folderPositionUnknown: "position unknown — may sit under another folder",
    expandItem: "Expand {name}",
    collapseItem: "Collapse {name}",
    hiddenAssignmentsKept:
      "They also have access to {count} project(s) that could not be loaded here. Saving keeps that access.",
    noProjects: "This organisation has no projects yet.",
    noProjectsForInvite:
      "This organisation has no projects yet. Create a project before inviting a member.",
    selectAtLeastOneProject: "Select at least one project for the member.",
    projectsUpdated: "Project access updated",
    projectsUpdateFailed: "Failed to update project access",
  },
  memberAccess: {
    description: "Choose which projects {name} can access, and what they can do in each.",
    projectsHeading: "Projects",
    noProjectSelected: "Select a project to choose what this member can do in it.",
    selectedProjectHint: "Settings for {project}",
    accessLevel: "Access level",
    presets: {
      view: "View only",
      contributor: "Contributor",
      full: "Full",
      custom: "Custom",
    },
    presetDescriptions: {
      view: "Can see files, issues, the brief and contacts, and use the drawing and model viewers, but cannot change anything.",
      contributor: "The Member default: can add and edit files and work on issues.",
      full: "Everything a member can be given in a project, including deleting permanently.",
      custom: "A combination chosen below.",
    },
    whatTheyCanDo: "What they can do",
    groups: {
      files: "Files",
      projectManagement: "Project management",
      viewers: "Viewers",
      brief: "Project brief",
      contacts: "Contacts",
    },
    permissions: {
      ADD_PROJECT_FILES: {
        label: "Upload files and create folders",
        description: "Add documents and new folders to this project.",
      },
      MODIFY_PROJECT_FILES: {
        label: "Edit, rename, move and move to Trash",
        description: "Change and reorganise files and folders, and move them to the Trash.",
      },
      DELETE_PROJECT_FILES: {
        label: "Delete permanently",
        description: "Remove files and folders from the Trash for good.",
      },
      VIEW_PM: {
        label: "See issues",
        description: "See this project's issues, schedule and board.",
      },
      MANAGE_PM: {
        label: "Create and edit issues",
        description: "Raise, update, reschedule and close issues.",
      },
      VIEW_DRAWING_COMPARE: {
        label: "Use drawing viewer",
        description:
          "Open this project's DWG drawings in the drawing viewer, on their own or side by side.",
      },
      USE_MODEL_VIEWER: {
        label: "Use model viewer",
        description: "Open this project's IFC models in the 3D model viewer.",
      },
      VIEW_BRIEFS: {
        label: "See the brief",
        description: "Read the project brief and its additions.",
      },
      MANAGE_BRIEFS: {
        label: "Edit the brief",
        description: "Change the brief and confirm new versions of it.",
      },
      VIEW_CONTACTS: {
        label: "View contacts",
        description: "See the people and companies listed for this project.",
      },
      MANAGE_CONTACTS: {
        label: "Add and edit contacts",
        description:
          "Add contacts to this project, and edit the ones they entered or that describe them.",
      },
    },
    requires: "Turning this on also turns on \u201c{permission}\u201d.",
    applyToAll: "Apply to all selected projects",
    appliedToAll: "Applied to every selected project",
    permissionsUnavailable:
      "Choosing what a member can do in each project is not available yet. You can still choose their projects and folders.",
    permissionsLoadFailed: "Could not load what this member can do in their projects.",
  },
  chatbotSettings: {
    title: "Chatbot settings",
    descriptionLine1:
      "When the chatbot wants to change your data it asks for confirmation each time.",
    descriptionLine2:
      "Turn a tool on here to always allow it and skip the confirmation card; turn it off to be asked again.",
    unavailable: "Chatbot settings are unavailable because the chatbot is not configured.",
    loadError: "Could not load your chatbot settings. Please try again.",
    updateError: "Could not update your chatbot setting. Please try again.",
    enableAria: "Always allow this action",
    disableAria: "Ask for confirmation each time",
    tools: {
      updateBuildingData: {
        label: "Update building data",
        description: "Let the chatbot edit building attribute values without asking each time.",
      },
      createPmIssue: {
        label: "Create issues",
        description: "Let the chatbot create project management issues without asking each time.",
      },
      updatePmIssue: {
        label: "Update issues",
        description: "Let the chatbot edit project management issues without asking each time.",
      },
      addPmIssueComment: {
        label: "Comment on issues",
        description: "Let the chatbot post comments on issues without asking each time.",
      },
      addFileToFolder: {
        label: "Add files to folders",
        description: "Let the chatbot file documents into folders without asking each time.",
      },
      setDocumentVerified: {
        label: "Verify documents",
        description:
          "Let the chatbot mark documents as verified without asking each time. Verifications are still recorded in your name.",
      },
    },
  },
};
